import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { StarIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import repoService, { RepositorySearchResult } from '../services/repoService';
import { getApiErrorMessage } from '../constants/messages';
import { useTranslation } from 'react-i18next';

interface RepositorySearcherProps {
  state: string;
  provider: string;
  onRepositorySelected: (repo: RepositorySearchResult) => void;
  isLoading?: boolean;
}

const RepositorySearcher: React.FC<RepositorySearcherProps> = ({
  state,
  provider,
  onRepositorySelected,
  isLoading: externalLoading = false
}) => {
  const { t } = (useTranslation as any)('builds');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [repositories, setRepositories] = useState<RepositorySearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const ITEMS_PER_PAGE = 50;

  // Recherche les repositories
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      // Si la recherche est vide, afficher les premiers repos
      setCurrentOffset(0);
      try {
        setIsLoading(true);
        const response = await repoService.listRepositories(state, ITEMS_PER_PAGE, 0);
        setRepositories(response.repositories);
        setHasMore(response.count === ITEMS_PER_PAGE);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error) || 'Erreur lors du chargement des repositories');
        setRepositories([]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      setCurrentOffset(0);
      const response = await repoService.searchRepositories(state, query, ITEMS_PER_PAGE, 0);
      setRepositories(response.repositories);
      setHasMore(response.count === ITEMS_PER_PAGE);
      setShowDropdown(true);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || 'Erreur lors de la recherche');
      setRepositories([]);
    } finally {
      setIsLoading(false);
    }
  }, [state]);

  // Debounce la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Charger plus de repos (pagination)
  const handleLoadMore = async () => {
    try {
      setIsLoading(true);
      const newOffset = currentOffset + ITEMS_PER_PAGE;
      const response = await repoService.searchRepositories(
        state,
        searchQuery || undefined,
        ITEMS_PER_PAGE,
        newOffset
      );

      setRepositories([...repositories, ...response.repositories]);
      setCurrentOffset(newOffset);
      setHasMore(response.count === ITEMS_PER_PAGE);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher les repos par défaut au chargement
  useEffect(() => {
    handleSearch('');
  }, [state]);

  const handleRepositoryClick = (repo: RepositorySearchResult) => {
    onRepositorySelected(repo);
    setShowDropdown(false);
    setSearchQuery('');
    setRepositories([]);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          placeholder={`Rechercher un repository sur ${provider}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (repositories.length > 0) {
              setShowDropdown(true);
            }
          }}
          disabled={externalLoading || isLoading}
          className="w-full px-4 py-3 border border-border-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:bg-surface-3"
        />

        {(isLoading || externalLoading) && (
          <div className="absolute right-3 top-3">
            <div className="animate-spin h-5 w-5 border-2 border-accent-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Dropdown avec résultats */}
      {showDropdown && repositories.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-surface-2 border border-border-1 rounded-lg shadow-lg max-h-96 overflow-y-auto z-10">
          {repositories.map((repo) => (
            <button
              key={repo.id}
              onClick={() => handleRepositoryClick(repo)}
              className="w-full text-left px-4 py-3 hover:bg-surface-3 border-b border-border-0 last:border-b-0 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-text-primary">
                    {repo.full_name}
                  </div>
                  {repo.description && (
                    <div className="text-sm text-text-tertiary line-clamp-1">
                      {repo.description}
                    </div>
                  )}
                  <div className="flex gap-3 mt-1 text-xs text-text-tertiary">
                    {repo.language && <span><DocumentTextIcon className="w-3 h-3 inline mr-1" />{repo.language}</span>}
                    {repo.stars !== undefined && repo.stars > 0 && (
                      <span><StarIcon className="w-3 h-3 inline mr-1" />{repo.stars}</span>
                    )}
                    {repo.private && <span className="text-red-600"><LockClosedIcon className="w-3 h-3 inline mr-1" />Private</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}

          {/* Bouton "Charger plus" */}
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="w-full px-4 py-3 text-center text-accent-400 hover:bg-surface-3 border-t border-border-0 font-medium disabled:opacity-50"
            >
              {isLoading ? 'Chargement...' : 'Charger plus'}
            </button>
          )}
        </div>
      )}

      {/* Message si aucun résultat */}
      {showDropdown && repositories.length === 0 && !isLoading && searchQuery && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-surface-2 border border-border-1 rounded-lg shadow-lg p-4 text-center text-text-tertiary z-10">
          Aucun repository trouvé pour "{searchQuery}"
        </div>
      )}

      {/* Info sur la limite */}
      {!isLoading && repositories.length > 0 && (
        <div className="mt-2 text-sm text-text-tertiary text-center">
          {repositories.length} repository{repositories.length > 1 ? 's' : ''} trouvé{repositories.length > 1 ? 's' : ''}
          {searchQuery && ` pour "${searchQuery}"`}
        </div>
      )}
    </div>
  );
};

export default RepositorySearcher;
